import React from 'react'
import Link from 'next/link'
import { ChevronRight, Home } from 'lucide-react'
import { cn } from './utils'
import { useLocaleNavigation } from '@/lib/navigation'

export interface BreadcrumbItem {
  label: string
  href?: string
  isActive?: boolean
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[]
  separator?: React.ReactNode
  showHome?: boolean
  homeHref?: string
  homeLabel?: string,
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ 
    className, 
    items, 
    separator = <ChevronRight className="h-4 w-4" />,
    showHome = true,
    homeHref = '/',
    homeLabel = 'Home',
    ...props 
  }, ref) => {
    const { getNavigationPath } = useLocaleNavigation();

    const breadcrumbItems = showHome 
      ? [{ label: homeLabel, href: getNavigationPath('home'), isActive: false }, ...items]
      : items

    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn(
          'flex items-center space-x-1 text-sm text-muted-foreground',
          className
        )}
        {...props}
      >
        <ol className="flex items-center space-x-1">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1
            const isFirst = index === 0 && showHome
            
            return (
              <li key={index} className="flex items-center space-x-1">
                {/* Breadcrumb Item */}
                {item.href && !item.isActive ? (
                  <Link
                    href={item.href}
                    className={cn(
                      'flex items-center space-x-1 hover:text-foreground transition-colors',
                      isFirst && 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {isFirst && <Home className="h-4 w-4" />}
                    <span>{item.label}</span>
                  </Link>
                ) : (
                  <span
                    className={cn(
                      'flex items-center space-x-1',
                      isLast || item.isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
                    )}
                    aria-current={isLast || item.isActive ? 'page' : undefined}
                  >
                    {isFirst && <Home className="h-4 w-4" />}
                    <span>{item.label}</span>
                  </span>
                )}
                
                {/* Separator */}
                {!isLast && (
                  <span className="text-muted-foreground ml-1" aria-hidden="true">
                    {separator}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    )
  }
)

Breadcrumb.displayName = 'Breadcrumb'

export { Breadcrumb }
